import { PinViewerStatic, ThemeSettingsSelection } from '@lessonup/pin-renderer';
import { Pin, PinTheme } from '@lessonup/pins-shared';
import { Button, Divider, spacing, styled } from '@lessonup/ui-components';
import React, { useMemo } from 'react';
import { useEditorTranslation } from '../../../hooks/useEditorTranslation';
import { ThemeDetailHeader } from './ThemeDetailHeader';
import { ThemeSettingsPanel } from './ThemeSettingsPanel';
import { getAggregatedComponentTypeGroups, getPinsHaveCustomSettings, getSelectivelyStrippedPin } from './utils';

interface ThemeDetailViewProps {
  theme: PinTheme;
  pin: Pin;
  allPins: Pin[];
  pinWidth: number;
  selections: ThemeSettingsSelection;
  onSelectionChange: (selections: ThemeSettingsSelection) => void;
  applyingToAllSlides: boolean;
  onApplyToAllSlidesChange: (value: boolean) => void;
  onBack: () => void;
  onApply: () => void;
}

export const ThemeDetailView: React.FC<ThemeDetailViewProps> = ({
  theme,
  pin,
  allPins,
  pinWidth,
  selections,
  onSelectionChange,
  applyingToAllSlides,
  onApplyToAllSlidesChange,
  onBack,
  onApply,
}) => {
  const { t } = useEditorTranslation();

  const hasCustomSettings = useMemo(() => {
    const hasCustomPinSettings = getPinsHaveCustomSettings(allPins);
    const componentGroups = getAggregatedComponentTypeGroups(allPins, t, applyingToAllSlides);
    return hasCustomPinSettings || componentGroups.length > 0;
  }, [allPins, applyingToAllSlides, t]);

  const previewPin = useMemo(() => {
    return getSelectivelyStrippedPin(pin, selections);
  }, [pin, selections]);

  const previewKey = useMemo(
    () => `${selections.pinSettings.background}-${JSON.stringify(selections.componentSettings)}`,
    [selections.pinSettings.background, selections.componentSettings]
  );

  return (
    <StyledContainer>
      <ThemeDetailHeader themeName={theme.name} onBack={onBack} />

      <Divider orientation="horizontal" />

      <StyledPreviewSection>
        <StyledPreviewWrapper>
          <PinViewerStatic key={previewKey} pin={previewPin} width={pinWidth} isThumb={false} pinTheme={theme} />
        </StyledPreviewWrapper>
      </StyledPreviewSection>

      <Divider orientation="horizontal" />

      <ThemeSettingsPanel
        allPins={allPins}
        selections={selections}
        onSelectionChange={onSelectionChange}
        applyingToAllSlides={applyingToAllSlides}
        onApplyToAllSlidesChange={onApplyToAllSlidesChange}
        hasCustomSettings={hasCustomSettings}
      />

      <Divider orientation="horizontal" />

      <StyledFooter>
        <StyledButton buttonType="primary" onClick={onApply} resizing="fixed">
          {t('editor.themeModal.detail.applyButton')}
        </StyledButton>
      </StyledFooter>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.size0};
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const StyledPreviewSection = styled.div`
  padding: ${spacing.size16} ${spacing.size12};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledPreviewWrapper = styled.div`
  display: flex;
  justify-content: center;
  max-width: 100%;
`;

const StyledFooter = styled.div`
  padding: ${spacing.size12};
`;
